export const REVIEWABLE_EVENT_IDENTITY_ERROR =
  "Event is not ready for review; backend event identity normalization is required.";

export interface ReviewableEvent extends Record<string, any> {
  event_id: string;
  id: string;
  video_id: string;
  analysis_id: string;
  reviewable?: boolean;
  lifecycle_status?: string;
  source_chunk_id?: string;
  source_chunk_offset_s?: number;
  source_chunk_duration_s?: number;
  source_chunk_event_index?: number;
}

export interface FlatReviewableEvent {
  videoId: string;
  eventId: string;
  displayIdx: number;
  event: ReviewableEvent;
}

export const eventIdOf = (event: any): string | null => {
  const raw = event?.event_id;
  if (raw === null || raw === undefined) return null;
  const value = String(raw).trim();
  return value || null;
};

/**
 * Generate a deterministic synthetic event_id for legacy inline events that
 * lack a backend-assigned event_id. Format matches the convention from
 * AGENTS.md: `legacy-inline:v1:{storage_idx}:{description_digest}`.
 */
export const syntheticEventId = (event: any, storageIdx: number): string => {
  const desc = typeof event?.description === "string" ? event.description : "";
  // Simple 8-char hash from description for collision resistance within a video
  let hash = 0;
  for (let i = 0; i < desc.length; i++) {
    hash = ((hash << 5) - hash + desc.charCodeAt(i)) | 0;
  }
  const hex = (hash >>> 0).toString(16).padStart(8, "0");
  return `legacy-inline:v1:${storageIdx}:${hex}`;
};

/**
 * Return the event's real event_id if present, otherwise stamp a synthetic
 * legacy-inline ID onto the event object (mutating in place for efficiency)
 * and return that. This is the safe alternative to `requireEventId` for code
 * paths that must handle both V2 (externalized) and V1 (legacy inline) events.
 */
export const ensureEventId = (event: any, storageIdx: number): string => {
  const existing = eventIdOf(event);
  if (existing) return existing;
  const synthetic = syntheticEventId(event, storageIdx);
  event.event_id = synthetic;
  return synthetic;
};

/**
 * Local suffix appended to disambiguate backend events that arrive with
 * colliding `event_id`s (the chunk-merge pipeline can mint the same id for two
 * different events). The suffixed id exists only in viewer state — strip it
 * with `canonicalEventId` before addressing any backend endpoint.
 */
const DUPLICATE_EVENT_ID_SUFFIX = "::dup";
const DUPLICATE_EVENT_ID_RE = /::dup\d+$/;

/** Backend-addressable event id: viewer-local duplicate suffix removed. */
export const canonicalEventId = (eventRef: string): string =>
  String(eventRef).replace(DUPLICATE_EVENT_ID_RE, "");

/**
 * Return `events` with viewer-unique `event_id`/`id` values. The first
 * occurrence of each id keeps its backend id; later occurrences get a
 * deterministic `::dupN` suffix (stable across refetches as long as the
 * backend returns the array in the same order, which `sort_analysis_events`
 * guarantees). Without this, every id-keyed lookup — optimistic approval
 * patches, keyboard nav, React list keys — hits the wrong or both duplicates.
 */
export const dedupeEventIdentities = (events: any[]): any[] => {
  if (!Array.isArray(events) || events.length < 2) return events;
  const counts = new Map<string, number>();
  let hasDuplicate = false;
  for (const event of events) {
    const eventId = eventIdOf(event);
    if (!eventId) continue;
    const count = (counts.get(eventId) ?? 0) + 1;
    counts.set(eventId, count);
    if (count > 1) hasDuplicate = true;
  }
  if (!hasDuplicate) return events;
  const seen = new Map<string, number>();
  return events.map((event) => {
    const eventId = eventIdOf(event);
    if (!eventId) return event;
    const occurrence = (seen.get(eventId) ?? 0) + 1;
    seen.set(eventId, occurrence);
    if (occurrence === 1) return event;
    const uniqueId = `${eventId}${DUPLICATE_EVENT_ID_SUFFIX}${occurrence - 1}`;
    return { ...event, event_id: uniqueId, id: uniqueId };
  });
};

export const requireEventId = (event: any): string => {
  const eventId = eventIdOf(event);
  if (!eventId) {
    throw new Error(REVIEWABLE_EVENT_IDENTITY_ERROR);
  }
  return eventId;
};

export const isEventActionable = (event: any): boolean => {
  return event?.reviewable !== false;
};

export const findEventIndexById = (events: any[] | undefined, eventId: string): number => {
  if (!Array.isArray(events)) return -1;
  return events.findIndex((event) => eventIdOf(event) === eventId);
};

export const findEventById = (events: any[] | undefined, eventId: string): any | null => {
  const idx = findEventIndexById(events, eventId);
  return idx === -1 ? null : events![idx];
};

export const mergeEventById = (events: any[] | undefined, eventId: string, patch: any): any[] => {
  // Identity fields always win from the existing event: patches built from
  // backend responses echo the canonical id, which would undo the
  // duplicate-disambiguation suffix and re-collide two events.
  return (events || []).map((event) =>
    eventIdOf(event) === eventId
      ? {
          ...event,
          ...patch,
          event_id: event.event_id,
          ...(event.id != null ? { id: event.id } : {}),
        }
      : event,
  );
};

export const mergeEventsPreservingLocalReviewStateById = (incoming: any[], existing?: any[]) => {
  if (!Array.isArray(existing) || existing.length === 0) return incoming;
  const previousById = new Map(
    existing
      .map((event) => [eventIdOf(event), event] as const)
      .filter(([eventId]) => Boolean(eventId)),
  );
  return incoming.map((event) => {
    const prior = previousById.get(eventIdOf(event));
    if (!prior) return event;
    const merged = {
      ...event,
      approval: prior.approval ?? event.approval,
      event_comment: prior.event_comment ?? event.event_comment,
      event_ai_reasoning: prior.event_ai_reasoning ?? event.event_ai_reasoning,
      category: prior.category ?? event.category,
      annotated_thumbnail_url: prior.annotated_thumbnail_url ?? event.annotated_thumbnail_url,
    };
    if (
      (event.data && typeof event.data === "object") ||
      (prior.data && typeof prior.data === "object")
    ) {
      merged.data = {
        ...(event.data && typeof event.data === "object" ? event.data : {}),
        ...(prior.data && typeof prior.data === "object"
          ? {
              annotated_thumbnail_url:
                prior.data.annotated_thumbnail_url ?? event.data?.annotated_thumbnail_url,
              custom_thumbnail_gs_uri:
                prior.data.custom_thumbnail_gs_uri ?? event.data?.custom_thumbnail_gs_uri,
            }
          : {}),
      };
    }
    return merged;
  });
};
